'use client'

import { DateRange } from '@repo/sdk'
import {
  Button,
  Calendar,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/ui/components'

import './declare.ts'

import { SelectLoadStatus } from '@repo/ui/inputs'
import { Column } from '@tanstack/table-core'
import { format } from 'date-fns'
import { InputHTMLAttributes, useEffect, useState } from 'react'

export function FilterInputs<TData, TValue>({ column }: { column: Column<TData, TValue> }) {
  const columnFilterValue = column.getFilterValue()
  const { filterVariant } = column.columnDef.meta ?? {}

  switch (filterVariant) {
    case 'date':
      return <DateInput onChange={column.setFilterValue} value={columnFilterValue as string} />
    case 'range':
      return (
        <div>
          <div className="flex space-x-2">
            {/* See faceted column filters example for min max values functionality */}
            <DebouncedInput
              className="w-24 rounded border shadow"
              onChange={value =>
                column.setFilterValue((old: [number, number]) => [value, old?.[1]])
              }
              placeholder={`Min`}
              type="number"
              value={(columnFilterValue as [number, number])?.[0] ?? ''}
            />
            <DebouncedInput
              className="w-24 rounded border shadow"
              onChange={value =>
                column.setFilterValue((old: [number, number]) => [old?.[0], value])
              }
              placeholder={`Max`}
              type="number"
              value={(columnFilterValue as [number, number])?.[1] ?? ''}
            />
          </div>
          <div className="h-1" />
        </div>
      )
    case 'select':
      return (
        <select
          onChange={e => column.setFilterValue(e.target.value)}
          value={columnFilterValue?.toString()}
        >
          {/* See faceted column filters example for dynamic select options */}
          <option value="">All</option>
          <option value="complicated">complicated</option>
          <option value="relationship">relationship</option>
          <option value="single">single</option>
        </select>
      )
    case 'selectStatus':
      return (
        <SelectLoadStatus
          isAdmin={process.env.NEXT_PUBLIC_SOURCE === 'cargoking'}
          onChange={column.setFilterValue}
          value={''}
        />
      )
    default:
      return (
        <DebouncedInput
          onChange={value => column.setFilterValue(value)}
          placeholder={`Search...`}
          type="text"
          value={(columnFilterValue ?? '') as string}
        />
      )
  }
}

// A typical debounced input react component
function DebouncedInput({
  debounce = 200,
  onChange,
  value: initialValue,
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  debounce?: number
  onChange: (value: number | string) => void
  value: number | string
}) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value)
    }, debounce)

    return () => clearTimeout(timeout)
  }, [value])

  return (
    <Input className={'w-full'} {...props} onChange={e => setValue(e.target.value)} value={value} />
  )
}

const DateInput = ({ onChange, value }: { onChange: (value: string) => void; value: string }) => {
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined
    to: Date | undefined
  }>({
    from: undefined,
    to: undefined,
  })

  useEffect(() => {
    if (!value) return
    const [from, to] = value.split(',')
    setDateRange({
      from: new Date(from),
      to: new Date(to),
    })
  }, [])

  const resetFilters = () => {
    onChange('')
    setDateRange({ from: undefined, to: undefined })
  }

  useEffect(() => {
    if (!dateRange.from || !dateRange.to) return
    onChange(format(dateRange.from, 'yyyy-MM-dd') + ',' + format(dateRange.to, 'yyyy-MM-dd'))
  }, [dateRange])

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className="w-full" type="button" variant="outline">
          {dateRange.from?.toLocaleDateString()} - {dateRange.to?.toLocaleDateString('en-GB')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-max">
        <>
          <Calendar
            classNames={{
              selected: 'bg-brand',
            }}
            mode="range"
            onSelect={dateRange => setDateRange(dateRange as DateRange)}
            selected={dateRange as DateRange}
          />
          <div className="flex justify-end">
            <Button onClick={resetFilters} size="sm">
              Reset
            </Button>
          </div>
        </>
      </PopoverContent>
    </Popover>
  )
}
